import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

/**
 * Public verification page reached via the QR code printed on the AEP report.
 * Shows a PII-light card so the recipient can confirm the document is
 * authentic and matches the platform's records.
 */
export default function ValidateReport() {
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get(`/public/validate/${aid}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setErr(e.response?.data?.detail || "Relatório não encontrado"); });
    return () => { mounted = false; };
  }, [aid]);

  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F9FA] p-6">
        <i className="fa-solid fa-circle-exclamation text-5xl text-rose-500 mb-4"></i>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 text-center">
          Documento não validado
        </h1>
        <p className="text-slate-600 mt-2 text-center max-w-md">{err}</p>
        <Link to="/" className="btn btn-secondary mt-6">Voltar à página inicial</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Verificando…
      </div>
    );
  }

  const signed = Boolean(data.signed_at);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4 sm:p-8" data-testid="validate-page">
      <div className="bg-white max-w-2xl w-full shadow-2xl">
        <header className="p-6 sm:p-8 border-b-4 border-emerald-900 flex items-center gap-4">
          <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-12 w-auto" />
          <div>
            <div className="overline text-emerald-900">Validação de documento</div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
              Relatório AEP autenticado
            </h1>
          </div>
        </header>

        <div className="p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-3 p-4 border bg-emerald-50 border-emerald-200" data-testid="validate-status">
            <i className="fa-solid fa-shield-halved text-emerald-900 text-2xl"></i>
            <div>
              <div className="font-bold text-emerald-900">Documento encontrado no registro Sollute NR-01</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Este relatório foi gerado a partir de respostas anônimas tratadas conforme a LGPD.
              </div>
            </div>
          </div>

          <Field label="Empresa avaliada" value={data.empresa || "—"} testid="validate-empresa" />
          {data.cnpj && <Field label="CNPJ" value={data.cnpj} mono testid="validate-cnpj" />}
          <Field label="Avaliação" value={data.titulo || "—"} testid="validate-titulo" />
          {(data.ghe_setor || data.funcao) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {data.ghe_setor && <Field label="GHE / Setor" value={data.ghe_setor} testid="validate-ghe" />}
              {data.funcao && <Field label="Função" value={data.funcao} testid="validate-funcao" />}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Respostas registradas" value={String(data.response_count)} testid="validate-responses" mono />
            <Field label="Trabalhadores esperados" value={String(data.n_trabalhadores || "—")} testid="validate-expected" mono />
          </div>

          <div className="border-t border-slate-200 pt-5">
            <div className="overline mb-3">Assinaturas</div>
            {signed ? (
              <div className="space-y-2 text-sm" data-testid="validate-signed-block">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-700"></i>
                  <span>Documento assinado eletronicamente em <strong>{new Date(data.signed_at).toLocaleString("pt-BR")}</strong></span>
                </div>
                {data.company_signer && (
                  <div className="text-xs text-slate-600 ml-6">Empresa: <strong>{data.company_signer}</strong></div>
                )}
                {data.technical_signer && (
                  <div className="text-xs text-slate-600 ml-6">Responsável técnico: <strong>{data.technical_signer}</strong></div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-700" data-testid="validate-unsigned">
                <i className="fa-solid fa-circle-exclamation"></i>
                Assinaturas eletrônicas não foram registradas para este documento.
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-4 text-xs text-slate-500">
            <div className="flex justify-between items-center font-mono">
              <span>ID: {data.assessment_id}</span>
              <span>Status: {data.status}</span>
            </div>
          </div>
        </div>

        <footer className="p-6 sm:p-8 border-t border-slate-200 bg-slate-50 text-center text-xs text-slate-500">
          Sollute Ocupacional · Plataforma de Avaliação NR-01 · Riscos Psicossociais
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, mono = false, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className={`text-slate-900 ${mono ? "font-mono" : "font-semibold"}`}>{value}</div>
    </div>
  );
}
